import { isCentralizedWallet, isDecentralizedWallet } from '@/entities/assets/helpers/guards';
import { useIwEditingStore } from '@/entities/interwills';
import { PrivateDocumentDtoType } from '@/shared/lib/forms/documents/private';
import { Address } from '@/shared/lib/forms/form-wrapper.types';
import { CollectionName } from '@/shared/lib/sj-orm/constants';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { AssetCategory, AssetDto, OwnershipType } from '@/shared/lib/sj-orm/models/asset.dto';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import {
  BankDto,
  BrokerDto,
  ContactDto,
  ContactType,
  isBankContact,
  isBrokerContact,
} from '@/shared/lib/sj-orm/models/contacts';
import { DocumentType } from '@/shared/lib/sj-orm/models/document/document-base.dto';
import { PrivateDocumentDto } from '@/shared/lib/sj-orm/models/document/private.document.dto';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';
import { DateTimeIso } from '@/shared/types';

import { CreateAssetParams } from '.';

export const useAssetForm = ({
  category,
  isEditMode,
  editAssetId,
  selectedCurrency,
}: {
  category?: AssetCategory;
  editAssetId?: DtoId;
  isEditMode?: boolean;
  selectedCurrency: string;
}) => {
  const { assets: storeAssets, updateAssets } = useIwEditingStore();

  const sjDatabase = useSJDatabase();
  const editAsset = editAssetId ? sjDatabase.collections.assets?.findById(editAssetId) : undefined;

  const { keyPair } = useKeyPairStore();

  const buildUpdatedContactDto = (
    values: CreateAssetParams,
    data: ContactDto,
  ): BankDto | BrokerDto | undefined => {
    if (isBankContact(data)) {
      return {
        ...data,
        nickname: values.placeOfRecordContactsBankName ?? '',
        selectedCountry: values.placeOfRecordContactsBankCountry ?? '',
        bankAddress: values.placeOfRecordContactsBankAddress ?? '',
      };
    }
    if (isBrokerContact(data)) {
      return {
        ...data,
        nickname: values.placeOfRecordContactsBrokerName ?? '',
        selectedCountry: values.placeOfRecordContactsBrokerCountry ?? '',
        brokerAddress: values.placeOfRecordContactsBrokerAddress ?? '',
      };
    }
    return undefined;
  };

  const buildContactDto = (values: CreateAssetParams, isBank: boolean): BankDto | BrokerDto => {
    if (isBank) {
      return {
        id: sjDatabase.generateId(CollectionName.CONTACTS),
        nickname: values.placeOfRecordContactsBankName ?? '',
        selectedCountry: values.placeOfRecordContactsBankCountry ?? '',
        bankAddress: values.placeOfRecordContactsBankAddress ?? '',
        type: ContactType.BANK,
      };
    }
    return {
      id: sjDatabase.generateId(CollectionName.CONTACTS),
      nickname: values.placeOfRecordContactsBrokerName ?? '',
      selectedCountry: values.placeOfRecordContactsBrokerCountry ?? '',
      brokerAddress: values.placeOfRecordContactsBrokerAddress ?? '',
      type: ContactType.BROKER,
    };
  };

  const buildUpdatedCryptoDto = (values: CreateAssetParams, data: AssetDto) => {
    if (isCentralizedWallet(data)) {
      return {
        ...data,
        holder: values.placeOfRecordCryptoHolder ?? '',
        accountNumber: values.placeOfRecordCryptoAccount,
        loginStorageLocation: values.placeOfRecordCryptoStorage,
      };
    }
    if (isDecentralizedWallet(data)) {
      return {
        ...data,
        officialWalletName: values.placeOfRecordCryptoWalletName ?? '',
        deviceStorageLocation: values.placeOfRecordCryptoDeviceLocation ?? '',
        loginStorageLocation: values.placeOfRecordCryptoStorage ?? '',
      };
    }
    return undefined;
  };

  const bankCreateOrUpdate = async (
    _finalValues: CreateAssetParams,
  ): Promise<CreateAssetParams> => {
    let finalValues = _finalValues;
    let bankKey: keyof typeof finalValues = 'bank';
    if ('holder' in finalValues) {
      bankKey = 'holder';
    }
    if ('broker' in finalValues) {
      bankKey = 'broker';
    }

    if (
      bankKey &&
      finalValues.bankName &&
      finalValues.bankCounty &&
      finalValues.bankAddress &&
      finalValues[bankKey]
    ) {
      const contactBase: Omit<BrokerDto | BankDto, 'id'> = {
        nickname: finalValues.bankName,
        selectedCountry: finalValues.bankCounty,
        ...(finalValues.broker
          ? {
              type: ContactType.BROKER,
              brokerAddress: finalValues.bankAddress,
            }
          : {
              type: ContactType.BANK,
              bankAddress: finalValues.bankAddress,
            }),
      };

      // create
      if (finalValues[bankKey] === 'new') {
        const newContact = {
          id: sjDatabase.generateId(CollectionName.CONTACTS),
          ...contactBase,
        };

        const newBankContact = await sjDatabase.collections.contacts?.create(newContact, [
          keyPair?.publicKey || '',
        ]);

        finalValues[bankKey] = newBankContact?.id;

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { bankName, bankCounty, bankAddress, ...sanitazedValues } = finalValues;

        finalValues = sanitazedValues;
      } else {
        const bankId = finalValues[bankKey];
        if (bankId) {
          await sjDatabase.collections.contacts?.update({
            id: bankId,
            ...contactBase,
          });
        }
      }
    }
    return finalValues;
  };

  const receiptCreateOrUpdate = async (
    _finalValues: CreateAssetParams,
  ): Promise<CreateAssetParams> => {
    let finalValues = _finalValues;
    if (
      (finalValues.receipt || finalValues.purchaseReceipt) &&
      finalValues.receiptDocCountry &&
      finalValues.receiptDocDate &&
      finalValues.receiptDocName &&
      finalValues.receiptDocNumber &&
      finalValues.receiptDocType
    ) {
      const documentBase: Omit<PrivateDocumentDto, 'id'> = {
        country: finalValues.receiptDocCountry,
        startDate: finalValues.receiptDocDate as DateTimeIso, // TODO WA-3907 dates
        name: finalValues.receiptDocName,
        number: finalValues.receiptDocNumber,
        privateCategory: finalValues.receiptDocType,
        type: DocumentType.PRIVATE_DOCUMENTS,
      };

      if (finalValues.receipt === 'new' || finalValues.purchaseReceipt === 'new') {
        const newDocument = {
          id: sjDatabase.generateId(CollectionName.PRIVATE_DOCUMENTS),
          ...documentBase,
        };

        const document = await sjDatabase.collections.privateDocuments?.create(newDocument, [
          keyPair?.publicKey || '',
        ]);

        if (finalValues.purchaseReceipt) {
          finalValues.purchaseReceipt = document?.id;
        } else {
          finalValues.receipt = document?.id;
        }
      } else {
        const receiptId = finalValues.receipt || finalValues.purchaseReceipt;
        if (receiptId) {
          await sjDatabase.collections.privateDocuments?.update({
            id: receiptId,
            ...documentBase,
          });
        }
      }

      /* eslint-disable @typescript-eslint/no-unused-vars */
      const {
        receiptDocCountry,
        receiptDocDate,
        receiptDocName,
        receiptDocNumber,
        receiptDocType,
        ...sanitazedValues
      } = finalValues;
      /* eslint-enable @typescript-eslint/no-unused-vars */
      finalValues = sanitazedValues;
    }

    return finalValues;
  };

  const cryptoUpdate = async (_finalValues: CreateAssetParams): Promise<CreateAssetParams> => {
    let finalValues = _finalValues;
    if (finalValues.placeOfRecordCryptowallet) {
      const data = sjDatabase.collections.assets?.findById(finalValues.placeOfRecordCryptowallet);

      if (!data) return finalValues;

      const dto = buildUpdatedCryptoDto(finalValues, data);
      if (dto) await sjDatabase.collections.assets?.update(dto);
    }
    /* eslint-disable @typescript-eslint/no-unused-vars */
    const {
      placeOfRecordCryptoWalletName,
      placeOfRecordCryptoDeviceLocation,
      placeOfRecordCryptoStorage,
      placeOfRecordCryptoHolder,
      placeOfRecordCryptoAccount,
      ...sanitazedValues
    } = finalValues;
    /* eslint-enable @typescript-eslint/no-unused-vars */
    finalValues = sanitazedValues;
    return finalValues;
  };

  const financialInstitutionCreateOrUpdate = async (
    _finalValues: CreateAssetParams,
  ): Promise<CreateAssetParams> => {
    let finalValues = _finalValues;
    if (finalValues.placeOfRecordFinancialInstitution) {
      if (finalValues.placeOfRecordFinancialInstitution !== 'new') {
        const data = sjDatabase.collections.contacts?.findById(
          finalValues.placeOfRecordFinancialInstitution,
        );

        if (!data) return finalValues;

        const dto = buildUpdatedContactDto(finalValues, data);
        if (dto) await sjDatabase.collections.contacts?.update(dto);
      } else {
        const dto = buildContactDto(
          finalValues,
          finalValues.placeOfRecordContactsType === ContactType.BANK,
        );
        const res = await sjDatabase.collections.contacts?.create(dto, [keyPair?.publicKey || '']);

        if (res) finalValues.placeOfRecordFinancialInstitution = res.id;
      }
    }
    /* eslint-disable @typescript-eslint/no-unused-vars */
    const {
      placeOfRecordContactsType,
      placeOfRecordContactsBankName,
      placeOfRecordContactsBankCountry,
      placeOfRecordContactsBankAddress,
      placeOfRecordContactsBrokerName,
      placeOfRecordContactsBrokerCountry,
      placeOfRecordContactsBrokerAddress,
      ...sanitazedValues
    } = finalValues;
    /* eslint-enable @typescript-eslint/no-unused-vars */
    finalValues = sanitazedValues;
    return finalValues;
  };

  const placeOfRecordDocumentCreateOrUpdate = async (
    _finalValues: CreateAssetParams,
  ): Promise<CreateAssetParams> => {
    let finalValues = _finalValues;
    if (finalValues.placeOfRecordDocument && finalValues.placeOfRecordDocumentType) {
      if (finalValues.placeOfRecordDocument !== 'new') {
        const data = sjDatabase.collections.privateDocuments?.findById(
          finalValues.placeOfRecordDocument,
        );

        if (!data) return finalValues;
        const dto: PrivateDocumentDto & PrivateDocumentDtoType = {
          ...data,
          privateCategory: finalValues.placeOfRecordDocumentType,
          name: finalValues.placeOfRecordDocumentName ?? '',
          country: finalValues.placeOfRecordDocumentCountry ?? '',
          number: finalValues.placeOfRecordDocumentNumber,
          storageLocation: finalValues.placeOfRecordDocumentLocation,
        };
        await sjDatabase.collections.privateDocuments?.update(dto);
      } else {
        const dto: PrivateDocumentDtoType = {
          name: finalValues.placeOfRecordDocumentName ?? '',
          country: finalValues.placeOfRecordDocumentCountry ?? '',
          number: finalValues.placeOfRecordDocumentNumber,
          storageLocation: finalValues.placeOfRecordDocumentLocation,
        };
        const res = await sjDatabase.collections.privateDocuments?.create(
          {
            id: sjDatabase.generateId(CollectionName.PRIVATE_DOCUMENTS),
            privateCategory: finalValues.placeOfRecordDocumentType,
            type: DocumentType.PRIVATE_DOCUMENTS,
            ...dto,
          },
          [keyPair?.publicKey || ''],
        );

        if (res) finalValues.placeOfRecordDocument = res.id;
      }
    }
    /* eslint-disable @typescript-eslint/no-unused-vars */
    const {
      placeOfRecordDocumentType,
      placeOfRecordDocumentName,
      placeOfRecordDocumentCountry,
      placeOfRecordDocumentNumber,
      placeOfRecordDocumentLocation,
      ...sanitazedValues
    } = finalValues;
    /* eslint-enable @typescript-eslint/no-unused-vars */
    finalValues = sanitazedValues;
    return finalValues;
  };

  const transformAddressFields = (
    values: CreateAssetParams,
  ): { finalValues: CreateAssetParams; address: Address | undefined } => {
    const addressFields: Address = {};

    // Use district if it exists, otherwise use province (migration17 logic: district: district || province)
    if (values.district || values.province) {
      addressFields.district = values.district || values.province;
    }
    if (values.city) addressFields.city = values.city;
    if (values.street) addressFields.street = values.street;
    if (values.buildingNumber) addressFields.buildingNumber = values.buildingNumber;
    if (values.apartments) addressFields.apartment = values.apartments;

    const assetCategoriesWithAddress = [
      AssetCategory.RESIDENTIAL_REAL_ESTATE,
      AssetCategory.COMMERCIAL_REAL_ESTATE,
      AssetCategory.LAND,
      AssetCategory.LEGAL_ENTITIES,
      AssetCategory.PUBLIC_FUND,
      AssetCategory.MY_FUND,
    ];

    if (assetCategoriesWithAddress.includes(category!) && values.country) {
      addressFields.country = values.country;
    }

    const hasAddressFields = Object.keys(addressFields).some(
      (key) => addressFields[key as keyof typeof addressFields],
    );

    const address = hasAddressFields ? addressFields : undefined;

    /* eslint-disable @typescript-eslint/no-unused-vars */
    const {
      district,
      province,
      city,
      street,
      buildingNumber,
      apartments,
      ...finalValuesWithoutAddress
    } = values;
    /* eslint-enable @typescript-eslint/no-unused-vars */

    const finalValues = finalValuesWithoutAddress;

    return {
      finalValues,
      address,
    };
  };

  const handleCreateOrUpdateAsset = async (
    values: CreateAssetParams,
  ): Promise<AssetDto | undefined> => {
    if (!category) return undefined;

    let finalValues = { ...values };

    if (finalValues.value) {
      finalValues.purchaseValue = {
        from: selectedCurrency,
        value: Number(values.value),
      };

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { value, ...sanitazedValues } = finalValues;
      finalValues = sanitazedValues;
    }

    const { finalValues: finalValuesWithoutAddress, address } = transformAddressFields(finalValues);
    finalValues = finalValuesWithoutAddress as CreateAssetParams;

    if (address) {
      finalValues.address = address;
    }

    finalValues = await bankCreateOrUpdate(finalValues);

    finalValues = await receiptCreateOrUpdate(finalValues);

    finalValues = await financialInstitutionCreateOrUpdate(finalValues);

    finalValues = await cryptoUpdate(finalValues);

    finalValues = await placeOfRecordDocumentCreateOrUpdate(finalValues);

    for (const key of Object.keys(finalValues)) {
      if (finalValues[key as keyof typeof finalValues] === null) {
        finalValues[key as keyof typeof finalValues] = undefined;
      }
    }

    if (!isEditMode) {
      const newAsset: AssetDto = {
        id: sjDatabase.generateId(CollectionName.ASSETS),
        category,
        nickname: finalValues.nickName,
        ...finalValues,
      };
      const asset = await sjDatabase.collections.assets?.create(newAsset, [
        keyPair?.publicKey || '',
      ]);
      if (asset && asset.ownerShipType === OwnershipType.DIRECT) {
        updateAssets([...storeAssets, asset]);
      }
      return asset;
    } else if (editAsset) {
      const obj: AssetDto = {
        ...editAsset,
        ...finalValues,
      };

      const asset = await sjDatabase.collections.assets?.update(obj);

      if (asset) {
        const newStoreAssets = [...storeAssets];
        const oldAssetIdx = newStoreAssets.findIndex((item) => item.id === asset?.id);
        newStoreAssets[oldAssetIdx] = {
          ...asset,
          ownerId: newStoreAssets[oldAssetIdx].ownerId,
        };

        updateAssets(newStoreAssets.filter((item) => item.ownerShipType === OwnershipType.DIRECT));
      }
      return asset;
    }
    return undefined;
  };

  return { handleCreateOrUpdateAsset };
};
